import { Pow18 } from 'config'
import { BigNumber } from 'ethers'
import { e2w, w2e } from 'utils/number'
import {
  BASE_FEE_USD_VALUE,
  MAX_TRADE_FEE_RATE,
  TRADE_FEE_RATE_SCALE,
} from '../../constants/trade'

export type PerTenThousand = number
export const baseUSDFee: BigNumber = e2w(BASE_FEE_USD_VALUE)

export enum TradingFeeStatus {
  Normal, // Order fee rate is normal, can be used for order creation.
  Overflow, // Order fee rate is overflow, cannot be used for order creation.
}

export interface TradingFeeRate {
  maker: PerTenThousand
  taker: PerTenThousand
  status: TradingFeeStatus
}

/**
 * Calculates the trading fee rate based on the provided USD value.
 * Minimum trading fee rate is fetched from the `/trading/spots/fee/rate` and `/trading/contracts/fee/rate`.
 * Maximum trading fee rate is from the `config/trade.ts` file.
 *
 * @param usdValue The USD value used for calculating the trading fee rate, the USD value is (price * amount) from order data.
 * @param minMaker E.g. 10 means 0.1%, value must be greater than 0 and less than 255.
 * @param minTaker E.g. 10 means 0.1%, value must be greater than 0 and less than 255.
 * @returns The trading fee rate object containing the maker fee rate, taker fee rate, and status.
 */
export function calcTradingFeeRate(
  usdValue: BigNumber,
  minMaker: number = 0,
  minTaker: number = 0,
): TradingFeeRate {
  if (usdValue.isZero()) {
    return {
      maker: minMaker,
      taker: minTaker,
      status: TradingFeeStatus.Normal,
    }
  }

  const rate: number = Math.ceil(
    Number(
      w2e(
        baseUSDFee.mul(Pow18).div(usdValue).mul(TRADE_FEE_RATE_SCALE),
      ).toString(),
    ),
  )

  if (rate > MAX_TRADE_FEE_RATE) {
    return {
      maker: MAX_TRADE_FEE_RATE,
      taker: MAX_TRADE_FEE_RATE,
      status: TradingFeeStatus.Overflow,
    }
  }

  return {
    maker: rate < minMaker ? minMaker : rate,
    taker: rate < minTaker ? minTaker : rate,
    status: TradingFeeStatus.Normal,
  }
}
